import { ApolloError, useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import * as FormTemplate from "src/services/formTemplate";
import { Question } from "src/services/formTemplate";
import * as GQL from "src/types/graphql";
import { FormTemplatePropsForExport } from ".";
import {
  GET_METADATA_BY_FORM_TEMPLATE,
  GET_TAG_GROUPS_BY_ENROLLMENT_PERIOD,
} from "../graphql/queries";

export function useFormTemplatePropsForExport(formTemplateId?: uuid): {
  data: FormTemplatePropsForExport | null;
  loading: boolean;
  error: ApolloError | undefined;
} {
  const { data, loading, error } = useQuery<
    GQL.GetMetadataByFormTemplate,
    GQL.GetMetadataByFormTemplateVariables
  >(GET_METADATA_BY_FORM_TEMPLATE, {
    variables: {
      form_template_id: formTemplateId ?? "",
    },
    skip: !formTemplateId,
  });

  const enrollmentPeriodId = data?.form_template_by_pk?.enrollment_period_id;

  const {
    data: tagGroupsData,
    loading: tagGroupsLoading,
    error: tagGroupsError,
  } = useQuery<
    GQL.GetTagGroupsByEnrollmentPeriod,
    GQL.GetTagGroupsByEnrollmentPeriodVariables
  >(GET_TAG_GROUPS_BY_ENROLLMENT_PERIOD, {
    variables: {
      enrollment_period_id: enrollmentPeriodId ?? "",
    },
    skip: !enrollmentPeriodId,
  });

  const schoolAdminCheck = useSchoolAdmin();

  const transformedData = useMemo(() => {
    if (!data?.form_template_by_pk) return null;

    const formTemplate = FormTemplate.fromGQL(data.form_template_by_pk);

    // Get questions and verifications in correct order
    const questionIdsAndLabelsInOrder = Question.getQuestionIdsAndLabelsInOrder(
      formTemplate.sections,
      schoolAdminCheck.isSchoolAdmin
        ? {
            filterBy: "school",
            schoolIdSet: new Set(schoolAdminCheck.allSchoolIds),
          }
        : { filterBy: "none" }
    );

    const formVerifications =
      data.form_template_by_pk.form_verifications?.map((v) => ({
        id: v.id,
        label: v.label,
      })) || [];

    const tagGroups =
      tagGroupsData?.tag_group?.map((tg) => ({
        id: tg.id,
        label: tg.name,
      })) || [];

    const disclaimerTitle = formTemplate.sections.find(
      (section) => section.type === "DisclaimerSection"
    )?.title;

    // Determine form template config
    const isLotteryEnabled =
      data.form_template_by_pk.lottery_offers_enabled ?? false;
    const isRankingEnabled =
      formTemplate.sections.find(
        (section) => section.type === "SchoolRankingSection"
      )?.rankingEnabled ?? false;

    return {
      formTemplateId: data.form_template_by_pk.id,
      questionIdsAndLabelsInOrder,
      formVerifications,
      tagGroups,
      formTemplateConfig: {
        isLotteryEnabled,
        isRankingEnabled,
        disclaimerTitle,
      },
    };
  }, [data, tagGroupsData, schoolAdminCheck]);

  return {
    data: transformedData,
    loading: loading || tagGroupsLoading,
    error: error || tagGroupsError,
  };
}
